import { ScanSummary, ScanResult } from './types';
import { runUXScan } from './uxScan';
import { runPWAScan } from './pwaScan';
import { runSecurityScan } from './securityScan';
import { runSecurityFunctionsScan } from './securityFunctionsScan';
import { runViralScan } from './viralScan';
import { runSocialShareScan } from './socialShareScan';
import { runWordlistScan } from './wordlistScan';
import { runPuzzleRandomizationScan } from './puzzleRandomizationScan';
import { runDeterministicPuzzleScan } from './deterministicPuzzleScan';
import { runArchiveRandomizationScan } from './archiveRandomizationScan';
import { runBrandingScan } from './brandingScan';
import { runCloudLocalSyncScan } from './cloudLocalSyncScan';
import { runDBStatsIntegrityScan } from './dbStatsIntegrityScan';
import { runPWAInstallScan } from './pwaInstallScan';
import { runPerformanceScan } from './performanceScan';
import { runAccessibilityScan } from './accessibilityScan';
import { runMobileLayoutScan } from './mobileLayoutScan';
import { runGameFlowScan } from './gameFlowScan';
import { runDataIntegrityScan } from './dataIntegrityScan';
import { runMemoryLeakScan } from './memoryLeakScan';
import { runVisualRegressionScan } from './visualRegressionScan';

export interface MegaScanResult {
  scans: ScanSummary[];
  totalProblemsFound: number;
  totalProblemsFixed: number;
  timestamp: Date;
  duration: number;
  categorySummary: CategorySummary[];
}

export interface CategorySummary {
  name: string;
  pass: number;
  warn: number;
  fail: number;
  info: number;
  fixed: number;
  scans: string[];
}

// Scan categories for organized reporting
const SCAN_CATEGORIES: Record<string, { category: string; priority: number }> = {
  'Wordlist Health Scan': { category: 'Wordlist Health', priority: 1 },
  'Puzzle Randomization Scan': { category: 'Puzzle Randomization', priority: 2 },
  'Deterministic Puzzle Scan': { category: 'Puzzle Randomization', priority: 2 },
  'Archive Randomization Scan': { category: 'Puzzle Randomization', priority: 2 },
  'Viral + Share Logic Scan': { category: 'Viral + Share Logic', priority: 3 },
  'Social Sharing QA': { category: 'Viral + Share Logic', priority: 3 },
  'Security Surface Scan': { category: 'Security Surface', priority: 4 },
  'Security Functions Scan': { category: 'Security Surface', priority: 4 },
  'UX Navigation Scan': { category: 'UX & Performance', priority: 5 },
  'PWA Health Scan': { category: 'PWA & Install', priority: 6 },
  'PWA Install Scan': { category: 'PWA & Install', priority: 6 },
  'Branding Scan': { category: 'Branding & UI', priority: 7 },
  'Accessibility Scan': { category: 'Branding & UI', priority: 7 },
  'Mobile Layout Scan': { category: 'Branding & UI', priority: 7 },
  'Performance Scan': { category: 'UX & Performance', priority: 5 },
  'Cloud-Local Sync Scan': { category: 'Data Integrity', priority: 8 },
  'DB Stats Integrity Scan': { category: 'Data Integrity', priority: 8 },
  'Data Integrity Scan': { category: 'Data Integrity', priority: 8 },
  'Game Flow Scan': { category: 'Game Flow', priority: 9 },
  'Memory Leak Scan': { category: 'UX & Performance', priority: 5 },
  'Visual Regression Scan': { category: 'Branding & UI', priority: 7 }
};

function getCategorySummaries(scans: ScanSummary[]): CategorySummary[] {
  const categoryMap = new Map<string, CategorySummary>();
  
  scans.forEach(scan => {
    const categoryInfo = SCAN_CATEGORIES[scan.scanName] || { category: 'Other QA Scans', priority: 99 };
    const category = categoryInfo.category;
    
    if (!categoryMap.has(category)) {
      categoryMap.set(category, {
        name: category,
        pass: 0,
        warn: 0,
        fail: 0,
        info: 0,
        fixed: 0,
        scans: []
      });
    }
    
    const summary = categoryMap.get(category)!;
    summary.pass += scan.passCount;
    summary.warn += scan.warnCount;
    summary.fail += scan.failCount;
    summary.info += scan.infoCount;
    summary.fixed += scan.results.filter((r: ScanResult) => r.autoFixed).length;
    summary.scans.push(scan.scanName);
  });
  
  // Sort by priority
  return Array.from(categoryMap.values()).sort((a, b) => {
    const aPriority = Object.entries(SCAN_CATEGORIES).find(([, v]) => v.category === a.name)?.[1].priority || 99;
    const bPriority = Object.entries(SCAN_CATEGORIES).find(([, v]) => v.category === b.name)?.[1].priority || 99;
    return aPriority - bPriority;
  });
}

export async function runMegaScan(
  autoFix: boolean = false,
  onProgress?: (scanName: string) => void
): Promise<MegaScanResult> {
  console.log('\n' + '═'.repeat(70));
  console.log('🔍 MEGA-SCAN: Running All Scans Sequentially');
  console.log(`   Auto-Fix: ${autoFix ? '✅ ON' : '❌ OFF'}`);
  console.log('═'.repeat(70) + '\n');
  
  const startTime = Date.now();
  
  // Run all scans sequentially with progress callbacks
  onProgress?.('UX Scan');
  const uxSummary = runUXScan();
  
  onProgress?.('PWA Scan');
  const pwaSummary = await runPWAScan();
  
  onProgress?.('Security Scan');
  const securitySummary = runSecurityScan(autoFix);
  
  onProgress?.('Security Functions Scan');
  const securityFunctionsSummary = await runSecurityFunctionsScan(autoFix);
  
  onProgress?.('Viral Scan');
  const viralSummary = runViralScan(autoFix);
  
  onProgress?.('Social Share Scan');
  const socialShareSummary = runSocialShareScan();
  
  onProgress?.('Wordlist Scan');
  const wordlistSummary = runWordlistScan(autoFix);
  
  onProgress?.('Puzzle Randomization Scan');
  const puzzleRandomizationSummary = runPuzzleRandomizationScan();
  
  onProgress?.('Deterministic Puzzle Scan');
  const deterministicSummary = runDeterministicPuzzleScan(autoFix);
  
  onProgress?.('Archive Randomization Scan');
  const archiveRandomizationSummary = runArchiveRandomizationScan();
  
  onProgress?.('Branding Scan');
  const brandingSummary = runBrandingScan();
  
  onProgress?.('Cloud-Local Sync Scan');
  const cloudSyncSummary = await runCloudLocalSyncScan();
  
  onProgress?.('DB Stats Integrity Scan');
  const dbStatsSummary = await runDBStatsIntegrityScan();
  
  onProgress?.('PWA Install Scan');
  const pwaInstallSummary = runPWAInstallScan();
  
  onProgress?.('Performance Scan');
  const performanceSummary = runPerformanceScan();
  
  onProgress?.('Accessibility Scan');
  const accessibilitySummary = runAccessibilityScan();
  
  onProgress?.('Mobile Layout Scan');
  const mobileLayoutSummary = runMobileLayoutScan();
  
  onProgress?.('Game Flow Scan');
  const gameFlowSummary = runGameFlowScan();
  
  onProgress?.('Data Integrity Scan');
  const dataIntegritySummary = await runDataIntegrityScan();
  
  onProgress?.('Memory Leak Scan');
  const memoryLeakSummary = runMemoryLeakScan();
  
  onProgress?.('Visual Regression Scan');
  const visualRegressionSummary = runVisualRegressionScan();
  
  onProgress?.('Finalizing...');
  
  const scans = [
    uxSummary, 
    pwaSummary, 
    securitySummary,
    securityFunctionsSummary,
    viralSummary,
    socialShareSummary,
    wordlistSummary, 
    puzzleRandomizationSummary, 
    deterministicSummary,
    archiveRandomizationSummary,
    brandingSummary,
    cloudSyncSummary,
    dbStatsSummary,
    pwaInstallSummary,
    performanceSummary,
    accessibilitySummary,
    mobileLayoutSummary,
    gameFlowSummary,
    dataIntegritySummary,
    memoryLeakSummary,
    visualRegressionSummary
  ];
  
  // Calculate totals
  const totalPass = scans.reduce((sum, s) => sum + s.passCount, 0);
  const totalWarn = scans.reduce((sum, s) => sum + s.warnCount, 0);
  const totalFail = scans.reduce((sum, s) => sum + s.failCount, 0);
  const totalInfo = scans.reduce((sum, s) => sum + s.infoCount, 0);
  const totalProblemsFound = totalFail + totalWarn;
  const totalProblemsFixed = scans.reduce(
    (sum, scan) => sum + scan.results.filter((r: ScanResult) => r.autoFixed).length,
    0
  );
  
  const endTime = Date.now();
  const duration = (endTime - startTime) / 1000;
  
  // Get category summaries
  const categorySummary = getCategorySummaries(scans);
  
  // Print beautiful summary
  console.log('\n' + '═'.repeat(70));
  console.log('📊 MEGA-SCAN CATEGORY SUMMARY');
  console.log('═'.repeat(70));
  console.log('┌─────────────────────────┬──────┬──────┬──────┬──────┬─────────┐');
  console.log('│ Category                │ Pass │ Warn │ Fail │ Info │ Fixed   │');
  console.log('├─────────────────────────┼──────┼──────┼──────┼──────┼─────────┤');
  
  categorySummary.forEach(cat => {
    const name = cat.name.padEnd(23).substring(0, 23);
    const pass = String(cat.pass).padStart(4);
    const warn = String(cat.warn).padStart(4);
    const fail = String(cat.fail).padStart(4);
    const info = String(cat.info).padStart(4);
    const fixed = String(cat.fixed).padStart(7);
    console.log(`│ ${name} │ ${pass} │ ${warn} │ ${fail} │ ${info} │ ${fixed} │`);
  });
  
  console.log('└─────────────────────────┴──────┴──────┴──────┴──────┴─────────┘');
  
  console.log('\n' + '═'.repeat(70));
  console.log('📊 MEGA-SCAN TOTALS');
  console.log('═'.repeat(70));
  console.log(`✅ Total Passed:   ${totalPass}`);
  console.log(`⚠️  Total Warnings: ${totalWarn}`);
  console.log(`❌ Total Failed:   ${totalFail}`);
  console.log(`ℹ️  Total Info:     ${totalInfo}`);
  console.log(`🔧 Auto-Fixed:     ${totalProblemsFixed}`);
  console.log(`⏱️  Duration:       ${duration.toFixed(2)}s`);
  console.log('═'.repeat(70));
  
  // Print failed/warning checks
  if (totalFail > 0 || totalWarn > 0) {
    console.log('\n⚠️  ISSUES REQUIRING ATTENTION:');
    scans.forEach(scan => {
      const issues = scan.results.filter((r: ScanResult) => r.status === 'fail' || r.status === 'warn');
      if (issues.length > 0) {
        console.log(`\n  ${scan.scanName}:`);
        issues.forEach((issue: ScanResult) => {
          const icon = issue.status === 'fail' ? '❌' : '⚠️';
          const fixedNote = issue.autoFixed ? ' (auto-fixed)' : '';
          console.log(`    ${icon} ${issue.name}: ${issue.message}${fixedNote}`);
          if (issue.details) {
            console.log(`       └─ ${issue.details}`);
          }
        });
      }
    });
  }
  
  console.log('\n');
  
  return {
    scans,
    totalProblemsFound,
    totalProblemsFixed,
    timestamp: new Date(),
    duration,
    categorySummary
  };
}

// Export function to generate JSON/CSV export
export function exportMegaScanResults(result: MegaScanResult, format: 'json' | 'csv' = 'json'): string {
  if (format === 'json') {
    return JSON.stringify({
      timestamp: result.timestamp.toISOString(),
      duration: `${result.duration.toFixed(2)}s`,
      summary: {
        totalProblemsFound: result.totalProblemsFound,
        totalProblemsFixed: result.totalProblemsFixed,
        totalChecks: result.scans.reduce((sum, s) => sum + s.results.length, 0)
      },
      categories: result.categorySummary,
      scans: result.scans.map(s => ({
        name: s.scanName,
        pass: s.passCount,
        warn: s.warnCount,
        fail: s.failCount,
        info: s.infoCount,
        results: s.results
      }))
    }, null, 2);
  }
  
  // CSV format
  const rows = ['Category,Scan,Check,Status,Message,Details,AutoFixed'];
  result.scans.forEach(scan => {
    const category = SCAN_CATEGORIES[scan.scanName]?.category || 'Other';
    scan.results.forEach((r: ScanResult) => {
      rows.push([
        category,
        scan.scanName,
        r.name,
        r.status,
        `"${(r.message || '').replace(/"/g, '""')}"`,
        `"${(r.details || '').replace(/"/g, '""')}"`,
        r.autoFixed ? 'Yes' : 'No'
      ].join(','));
    });
  });
  
  return rows.join('\n');
}
